import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";

type Client = SupabaseClient<Database>;

export type FeedbackType = "bug" | "suggestion" | "praise" | "other";
export type FeedbackStatus = "open" | "in_progress" | "resolved" | "closed";

export interface FeedbackItem {
  id: string;
  created_at: string;
  updated_at: string;
  profile_id: string | null;
  user_email: string | null;
  user_name: string | null;
  type: FeedbackType;
  title: string;
  description: string;
  steps: string | null;
  page_url: string | null;
  user_agent: string | null;
  image_paths: string[];
  status: FeedbackStatus;
  admin_notes: string | null;
}

export interface FeedbackCreate {
  user_email?: string;
  type: FeedbackType;
  title: string;
  description: string;
  steps?: string;
  page_url?: string;
  user_agent?: string;
  image_paths?: string[];
}

export interface FeedbackFilter {
  type?: FeedbackType;
  status?: FeedbackStatus;
  search?: string;
  limit?: number;
}

const BUCKET = "feedback-images";
const SIGNED_URL_TTL_SECONDS = 60 * 15; // 15 min — Sec5

/**
 * Uploads a feedback screenshot.
 *
 * Sec5 lockdown: bucket is private and RLS requires the first path segment
 * to equal `auth.uid()::text` (= profiles.id UUID). Callers MUST pass the
 * profile UUID, not the Clerk user id, and MUST use the authenticated
 * Supabase client.
 */
export async function uploadFeedbackImage(
  client: Client,
  file: File,
  profileId: string,
): Promise<string> {
  if (!profileId) throw new Error("Sessão não pronta para upload.");
  const ext = file.name.split(".").pop() ?? "png";
  const path = `${profileId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await client.storage.from(BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (error) throw new Error(`Upload falhou: ${error.message}`);
  return path;
}

/**
 * Returns a short-lived signed URL for a feedback screenshot. The bucket is
 * private so `getPublicUrl()` is no longer valid (Sec5).
 */
export async function getFeedbackImageSignedUrl(
  client: Client,
  path: string,
): Promise<string> {
  const { data, error } = await client.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) {
    throw new Error(`Falha ao gerar URL: ${error?.message ?? "unknown"}`);
  }
  return data.signedUrl;
}

export async function createFeedback(
  token: string,
  payload: FeedbackCreate,
): Promise<{ id: string }> {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL não configurado");

  const resp = await fetch(`${supabaseUrl.replace(/\/$/, "")}/functions/v1/submit-feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error ?? `HTTP ${resp.status}`);
  }

  return resp.json();
}

export async function fetchFeedback(
  client: Client,
  filter: FeedbackFilter = {},
): Promise<FeedbackItem[]> {
  let query = (client.from("feedback" as never) as ReturnType<typeof client.from>)
    .select("*")
    .order("created_at", { ascending: false })
    .limit(filter.limit ?? 200);

  if (filter.type) query = query.eq("type", filter.type);
  if (filter.status) query = query.eq("status", filter.status);
  if (filter.search) {
    query = query.or(
      `title.ilike.%${filter.search}%,description.ilike.%${filter.search}%,user_email.ilike.%${filter.search}%`,
    );
  }

  const { data, error } = await query;
  if (error) throw new Error(`Erro ao buscar feedbacks: ${error.message}`);

  const items = (data ?? []) as FeedbackItem[];

  // Enrich rows missing user info with profiles data (covers pre-backfill records).
  const missingIds = [
    ...new Set(
      items
        .filter((i) => (!i.user_name || !i.user_email) && i.profile_id)
        .map((i) => i.profile_id as string),
    ),
  ];

  if (missingIds.length > 0) {
    const { data: profiles } = await client
      .from("profiles")
      .select("id, email, display_name")
      .in("id", missingIds);

    if (profiles) {
      const byProfileId = Object.fromEntries(
        (profiles as Array<{ id: string; email: string | null; display_name: string | null }>)
          .map((p) => [p.id, p]),
      );
      for (const item of items) {
        const prof = item.profile_id ? byProfileId[item.profile_id] : undefined;
        if (!prof) continue;
        if (!item.user_name) item.user_name = prof.display_name ?? null;
        if (!item.user_email) item.user_email = prof.email ?? null;
      }
    }
  }

  return items;
}

export async function deleteFeedback(
  client: Client,
  ids: string[],
): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await (client.from("feedback" as never) as ReturnType<typeof client.from>)
    .delete()
    .in("id", ids);
  if (error) throw new Error(`Erro ao excluir feedback: ${error.message}`);
}

export async function updateFeedbackStatus(
  client: Client,
  id: string,
  status: FeedbackStatus,
  admin_notes?: string,
): Promise<void> {
  const update: Record<string, unknown> = { status };
  if (admin_notes !== undefined) update.admin_notes = admin_notes;

  const { error } = await (client.from("feedback" as never) as ReturnType<typeof client.from>)
    .update(update)
    .eq("id", id);

  if (error) throw new Error(`Erro ao atualizar feedback: ${error.message}`);
}
